import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react"

import {
  useRevertTestSetVersionTransport,
  useTestSetMetadataTransport,
  useTestSetRecordsTransport,
  useTestSetVersionsTransport,
} from "@/test-set/transport"

import type { TestCaseRecord } from "@/types/api/TestCaseRecord"
import type { TestSetRecord } from "@/types/api/TestSetRecord"

import {
  CURRENT_VERSION_KEY,
  type TestSetVersionResource,
} from "../utils/testSetVersionDisplay"

interface TestSetVersionHistoryContextType {
  isActive: boolean
  open: () => void
  close: () => void
  currentRecord: TestSetRecord | null
  currentRows: TestCaseRecord[]
  versions: TestSetVersionResource[]
  isLoading: boolean
  isError: boolean
  selectedKey: string
  selectVersion: (key: string) => void
  revertToVersion: (versionId: string) => Promise<void>
  isReverting: boolean
}

const TestSetVersionHistoryContext = createContext<
  TestSetVersionHistoryContextType | undefined
>(undefined)

/**
 * Drives the version-history mode for the test-set editor. Mirrors the metrics
 * version-history context: opening swaps the editor for a read-only view of the
 * selected version. The live (saved) test set is the current version (copy-on-save
 * model) and is fetched alongside the prior-version history.
 *
 * Both fetches are deferred until the mode is opened (`enabled: isActive`).
 */
export const TestSetVersionHistoryProvider: React.FC<{
  testSetId: string
  children: ReactNode
}> = ({ testSetId, children }) => {
  const [isActive, setIsActive] = useState(false)
  const [selectedKey, setSelectedKey] = useState<string>(CURRENT_VERSION_KEY)

  const { data: currentData, isLoading: isCurrentLoading } =
    useTestSetMetadataTransport(testSetId, { enabled: isActive })
  const { data: currentRecords, isLoading: isRecordsLoading } =
    useTestSetRecordsTransport(
      { test_set_id: testSetId },
      { enabled: isActive },
    )
  const {
    versions,
    isLoading: isVersionsLoading,
    isError,
  } = useTestSetVersionsTransport(testSetId, { enabled: isActive })
  const revertMutation = useRevertTestSetVersionTransport()

  useEffect(() => {
    setIsActive(false)
    setSelectedKey(CURRENT_VERSION_KEY)
  }, [testSetId])

  const open = useCallback(() => {
    setSelectedKey(CURRENT_VERSION_KEY)
    setIsActive(true)
  }, [])

  const close = useCallback(() => {
    setIsActive(false)
    setSelectedKey(CURRENT_VERSION_KEY)
  }, [])

  const selectVersion = useCallback((key: string) => {
    setSelectedKey(key)
  }, [])

  const revertToVersion = useCallback(
    async (versionId: string) => {
      await revertMutation.mutateAsync({
        test_set_id: testSetId,
        version_id: versionId,
      })
    },
    [revertMutation, testSetId],
  )

  const currentRecord = currentData?.metadata ?? null
  const currentRows = useMemo(() => currentRecords ?? [], [currentRecords])

  const value = useMemo(
    () => ({
      isActive,
      open,
      close,
      currentRecord,
      currentRows,
      versions,
      isLoading: isVersionsLoading || isCurrentLoading || isRecordsLoading,
      isError,
      selectedKey,
      selectVersion,
      revertToVersion,
      isReverting: revertMutation.isPending,
    }),
    [
      isActive,
      open,
      close,
      currentRecord,
      currentRows,
      versions,
      isVersionsLoading,
      isCurrentLoading,
      isRecordsLoading,
      isError,
      selectedKey,
      selectVersion,
      revertToVersion,
      revertMutation.isPending,
    ],
  )

  return (
    <TestSetVersionHistoryContext.Provider value={value}>
      {children}
    </TestSetVersionHistoryContext.Provider>
  )
}

export const useTestSetVersionHistory =
  (): TestSetVersionHistoryContextType => {
    const context = useContext(TestSetVersionHistoryContext)
    if (!context) {
      throw new Error(
        "useTestSetVersionHistory must be used within TestSetVersionHistoryProvider",
      )
    }
    return context
  }
